import { Fragment, useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Download, Search, Trash2, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { PageHeader, Loading, Empty, StatPill, Confirm } from "@/components/common";
import { DebtBadge, PaymentDialog } from "@/components/PaymentDialog";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg, downloadCsv } from "@/lib/api";
import { fmtDate, num, periodLabel, rupiah } from "@/lib/format";

function PaymentHistory({ customerId, reloadKey, onChanged }) {
  const { can } = useAuth();
  const [rows, setRows] = useState(null);
  useEffect(() => {
    api.get("/payments", { params: { customer_id: customerId } }).then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [customerId, reloadKey]);
  const del = async (id) => {
    try { await api.delete(`/payments/${id}`); toast.success("Pembayaran dihapus"); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!rows) return <Loading />;
  if (!rows.length) return <p className="text-xs text-slate-500 py-2">Belum ada pembayaran.</p>;
  return (
    <table className="xl">
      <thead><tr><th>Tanggal</th><th>Periode</th><th className="text-right">Jumlah</th><th>Metode</th><th>Catatan</th><th>Dicatat oleh</th>{can("payment:delete") && <th />}</tr></thead>
      <tbody>{rows.map((p) => (
        <tr key={p.id} data-testid={`payment-row-${p.id}`}>
          <td className="text-xs">{fmtDate(p.date)}</td><td className="text-xs">{periodLabel(p.period)}</td><td className="num">{num(p.amount)}</td>
          <td className="text-xs capitalize">{p.method}</td><td className="text-xs">{p.note}</td><td className="font-mono text-xs">{p.recorded_by}</td>
          {can("payment:delete") && <td><Confirm title="Hapus pembayaran ini?" description={`${rupiah(p.amount)} · hutang akan bertambah kembali`} onConfirm={() => del(p.id)} testId="payment-delete"
            trigger={<Button size="sm" variant="ghost" className="text-rose-600 h-7" data-testid={`payment-delete-${p.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>} /></td>}
        </tr>
      ))}</tbody>
    </table>
  );
}

function DebtRow({ d, i, onPay, reloadKey, onChanged }) {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <Fragment>
      <tr className="cursor-pointer" onClick={() => setOpen(!open)} data-testid={`debt-row-${i}`}>
        <td className="rownum">{open ? <ChevronDown className="h-3 w-3 mx-auto" /> : <ChevronRight className="h-3 w-3 mx-auto" />}</td>
        <td className="font-medium">{d.customer_name}</td>
        <td className="font-mono text-xs">{d.phone}</td>
        <td className="text-xs text-slate-600">{d.address}</td>
        <td className="num">{d.debt_periods}</td>
        <td className="text-xs">{d.oldest_period ? periodLabel(d.oldest_period) : "-"}</td>
        <td className="num font-semibold text-rose-700" data-testid={`debt-remaining-${i}`}>{num(d.remaining)}</td>
      </tr>
      {open && (
        <tr><td colSpan={7} className="bg-slate-50 p-3 whitespace-normal">
          <div className="grid lg:grid-cols-2 gap-4">
            <table className="xl bg-white">
              <thead><tr><th>Periode</th><th className="text-right">Tagihan</th><th className="text-right">Dibayar</th><th className="text-right">Sisa</th><th>Status</th>{can("payment:write") && <th />}</tr></thead>
              <tbody>{d.periods.map((x) => (
                <tr key={x.period}>
                  <td>{periodLabel(x.period)}</td><td className="num">{num(x.due)}</td><td className="num">{num(x.paid)}</td><td className="num text-rose-700">{num(Math.max(x.remaining, 0))}</td>
                  <td><DebtBadge status={x.status} /></td>
                  {can("payment:write") && <td>{x.remaining > 0 && <Button size="sm" className="h-7 bg-emerald-700 hover:bg-emerald-800" onClick={(e) => { e.stopPropagation(); onPay({ ...x, customer_id: d.customer_id, customer_name: d.customer_name }); }} data-testid={`debt-pay-${i}-${x.period}`}>Bayar</Button>}</td>}
                </tr>
              ))}</tbody>
            </table>
            <div><div className="text-xs font-semibold text-slate-700 mb-1">Riwayat Pembayaran</div><PaymentHistory customerId={d.customer_id} reloadKey={reloadKey} onChanged={onChanged} /></div>
          </div>
        </td></tr>
      )}
    </Fragment>
  );
}

function DebtCard({ d, i, onPay, reloadKey, onChanged }) {
  const { can } = useAuth();
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-slate-300 bg-white rounded-sm" data-testid={`debt-card-${i}`}>
      <button type="button" className="w-full text-left p-3 space-y-1" onClick={() => setOpen(!open)} data-testid={`debt-card-toggle-${i}`}>
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0"><div className="font-medium">{d.customer_name}</div><div className="text-xs text-slate-600">{d.address || "-"}</div></div>
          <div className="text-right"><div className="font-mono font-semibold text-rose-700">{rupiah(d.remaining)}</div><div className="text-[10px] text-slate-500">{d.debt_periods} periode{d.oldest_period ? ` · sejak ${periodLabel(d.oldest_period)}` : ""}</div></div>
          {open ? <ChevronDown className="h-4 w-4 mt-1" /> : <ChevronRight className="h-4 w-4 mt-1" />}
        </div>
      </button>
      {d.phone && <div className="px-3 pb-2"><a href={`tel:${d.phone}`} className="inline-flex items-center h-10 px-3 rounded-md border border-emerald-300 text-emerald-800 text-sm font-mono" data-testid={`debt-call-${i}`}>{d.phone}</a></div>}
      {open && (
        <div className="border-t border-slate-200 p-3 space-y-2 bg-slate-50">
          {d.periods.map((x) => (
            <div key={x.period} className="bg-white border border-slate-200 rounded-sm p-2 flex items-center gap-2">
              <div className="flex-1 text-xs"><div className="font-semibold">{periodLabel(x.period)} <DebtBadge status={x.status} /></div><div className="text-slate-600">Tagihan {num(x.due)} · Dibayar {num(x.paid)}</div><div className="text-rose-700 font-mono">Sisa {num(Math.max(x.remaining, 0))}</div></div>
              {can("payment:write") && x.remaining > 0 && <Button className="h-11 px-5 bg-emerald-700 hover:bg-emerald-800" onClick={() => onPay({ ...x, customer_id: d.customer_id, customer_name: d.customer_name })} data-testid={`debt-card-pay-${i}-${x.period}`}>Bayar</Button>}
            </div>
          ))}
          <div className="text-xs font-semibold text-slate-700">Riwayat Pembayaran</div>
          <div className="xl-wrap"><PaymentHistory customerId={d.customer_id} reloadKey={reloadKey} onChanged={onChanged} /></div>
        </div>
      )}
    </div>
  );
}

export default function Debts() {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [showPaid, setShowPaid] = useState(false);
  const [target, setTarget] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const load = useCallback(async () => {
    try { setData((await api.get("/debts", { params: { include_paid: showPaid } })).data); setReloadKey((k) => k + 1); } catch (e) { toast.error(errMsg(e)); }
  }, [showPaid]);
  useEffect(() => {
    load();
  }, [load]);
  const rows = (data?.rows || []).filter((d) => d.customer_name.toLowerCase().includes(q.toLowerCase()));
  const oldest = (data?.rows || []).map((d) => d.oldest_period).filter(Boolean).sort()[0];
  return (
    <div>
      <PageHeader sheet="SHEET 04" title="Daftar Hutang Pelanggan" subtitle="Hutang dihitung dari nilai rekap penjualan per periode dikurangi pembayaran yang dicatat.">
        {can("export:run") && <Button variant="outline" onClick={() => downloadCsv("/debts/export", "daftar-hutang")} data-testid="debt-export-btn"><Download className="h-4 w-4 mr-2" />Export CSV</Button>}
      </PageHeader>
      {!data ? <Loading /> : (
        <div className="space-y-3 fade-up">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            <StatPill label="Total Hutang" value={rupiah(data.total_remaining)} tone="amber" testId="debt-stat-total" />
            <StatPill label="Pelanggan Berhutang" value={num(data.debtors)} testId="debt-stat-debtors" />
            <StatPill label="Hutang Tertua" value={oldest ? periodLabel(oldest) : "-"} testId="debt-stat-oldest" />
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <div className="relative w-full sm:w-auto"><Search className="h-4 w-4 absolute left-2.5 top-2.5 text-slate-400" /><Input placeholder="Cari pelanggan..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-8 w-full sm:w-64" data-testid="debt-search-input" /></div>
            <label className="flex items-center gap-2 text-sm"><Switch checked={showPaid} onCheckedChange={setShowPaid} data-testid="debt-show-paid" />Tampilkan periode yang sudah lunas</label>
          </div>
          {!rows.length ? <Empty><Wallet className="h-6 w-6 mx-auto mb-2 text-emerald-600" />Tidak ada hutang. Semua pelanggan sudah lunas.</Empty> : (
            <>
            <div className="md:hidden space-y-2" data-testid="debt-cards">
              {rows.map((d, i) => <DebtCard key={d.customer_id} d={d} i={i} onPay={setTarget} reloadKey={reloadKey} onChanged={load} />)}
              <div className="text-right text-sm font-semibold">TOTAL <span className="font-mono text-rose-700">{rupiah(rows.reduce((s, d) => s + d.remaining, 0))}</span></div>
            </div>
            <div className="xl-wrap hidden md:block">
              <table className="xl" data-testid="debt-table">
                <thead><tr><th className="rownum" /><th>Pelanggan</th><th>Telepon</th><th>Alamat</th><th className="text-right">Periode Hutang</th><th>Sejak</th><th className="text-right">Sisa Hutang (Rp)</th></tr></thead>
                <tbody>{rows.map((d, i) => <DebtRow key={d.customer_id} d={d} i={i} onPay={setTarget} reloadKey={reloadKey} onChanged={load} />)}</tbody>
                <tfoot><tr><td className="rownum">Σ</td><td colSpan={5}>TOTAL</td><td className="num">{num(rows.reduce((s, d) => s + d.remaining, 0))}</td></tr></tfoot>
              </table>
            </div>
            </>
          )}
        </div>
      )}
      <PaymentDialog target={target} onClose={() => setTarget(null)} onDone={load} />
    </div>
  );
}
